
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

// Angular Material Imports
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AuthService } from '../../service/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { PasswordResetDialogComponent } from '../password-reset-dialog/password-reset-dialog.component';
import { NewPasswordDialogComponent } from '../new-password-dialog/new-password-dialog.component';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  standalone: true, // This is the key difference!
  imports: [
    FormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    RouterModule
  ],
})
export class LoginComponent implements OnInit {
  private dialog = inject(MatDialog);

  username = '';
  password = '';
  isSSOEnabled = environment.sso;

  // Shown under "Sign in" so users can tell which organization's site they're on.
  readonly orgFormalName: string = (environment as any).orgFormalName || '';
  readonly currentYear = new Date().getFullYear();

  showPassword = false;
  // Signals, not plain fields: the app is zoneless, so a plain field set after
  // the awaited Cognito call never repaints (a wrong password showed nothing).
  readonly signingIn = signal(false);
  readonly errorMessage = signal('');

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  /**
   * Where to land after a successful sign-in.
   *
   * The guard puts the originally-requested URL in `?returnUrl=`. We stash it
   * in sessionStorage because the SSO path leaves the app entirely (hosted UI
   * redirect) and comes back on a URL that no longer carries our query param —
   * a field on the component would not survive that round trip.
   */
  private static readonly RETURN_URL_KEY = 'hc.login.returnUrl';
  private static readonly DEFAULT_AFTER_LOGIN = '/securehome/my-work';

  private stashReturnUrl(): void {
    const target = this.route.snapshot.queryParamMap.get('returnUrl');
    if (target && LoginComponent.isSafeInternalUrl(target)) {
      try {
        sessionStorage.setItem(LoginComponent.RETURN_URL_KEY, target);
      } catch { /* private mode / storage disabled — fall back to the default */ }
    }
  }

  /**
   * Only same-origin, absolute-path URLs. Rejecting `//host` (protocol-relative)
   * and anything with a scheme is what keeps `?returnUrl=` from becoming an
   * open redirect that mails users to an attacker's login page.
   */
  private static isSafeInternalUrl(url: string): boolean {
    return url.startsWith('/') && !url.startsWith('//');
  }

  private consumeReturnUrl(): string {
    let target: string | null = null;
    try {
      target = sessionStorage.getItem(LoginComponent.RETURN_URL_KEY);
      sessionStorage.removeItem(LoginComponent.RETURN_URL_KEY);
    } catch { /* unreadable storage is not a login failure */ }
    return target && LoginComponent.isSafeInternalUrl(target)
      ? target
      : LoginComponent.DEFAULT_AFTER_LOGIN;
  }

  ngOnInit() {
    // Capture the guard's returnUrl before anything can navigate away — the SSO
    // branch below leaves the app immediately.
    this.stashReturnUrl();

    // If SSO is enabled, handle authentication flow.
    // isAuthenticated() awaits any in-flight SSO callback (code exchange), so a
    // successful redirect lands us authenticated here → the requested page;
    // otherwise we kick off the hosted-UI login.
    if (this.isSSOEnabled) {
      this.authService.isAuthenticated().then(isAuth => {
        if (isAuth) {
          this.router.navigateByUrl(this.consumeReturnUrl());
        } else {
          this.authService.loginWithSSO();
        }
      });
    }
  }

async onLogin() {
    // If SSO is enabled, redirect to SSO login instead of username/password
    if (this.isSSOEnabled) {
      this.authService.loginWithSSO();
      return;
    }

    // Non-SSO login (username/password)
    if (!this.username || !this.password || this.signingIn()) return;
    this.errorMessage.set('');
    this.signingIn.set(true);
    try {
      const result = await this.authService.signIn(this.username, this.password);

      //console.log('Authentication result received:', result);

      // Check if new password is required
      if (result === 'NEW_PASSWORD_REQUIRED') {
        this.handleNewPasswordRequired();
        return;
      }

      // Check if MFA is required
      if (result === 'MFA_REQUIRED') {
        alert('Multi-factor authentication is required but not yet implemented.');
        return;
      }

      // The result is a CognitoUserSession object
      if (result && typeof result.getIdToken === 'function') {
        this.handleSuccessfulLogin(result);
      } else {
        console.warn('Login failed: An unexpected result was returned.');
        this.errorMessage.set('Sign-in failed. Please try again.');
      }
    } catch (error: any) {
        console.error('Login failed during the process:', error);

        let errorMessage = 'An unknown error occurred. Please try again.';

        // Check for specific Cognito errors
        if (error.name === 'UserNotFoundException') {
            errorMessage = 'Incorrect username or password.';
        } else if (error.name === 'NotAuthorizedException') {
            errorMessage = 'Incorrect username or password.';
        } else if (error.name === 'CodeMismatchException') {
            errorMessage = 'Invalid verification code.';
        } else if (error.message) {
            errorMessage = error.message;
        }

        this.errorMessage.set(errorMessage);
    } finally {
        this.signingIn.set(false);
    }
}

  /**
   * Handles the NEW_PASSWORD_REQUIRED challenge by showing a dialog
   */
  private handleNewPasswordRequired(): void {
    const dialogRef = this.dialog.open(NewPasswordDialogComponent, {
      width: '400px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(async (newPassword: string | null) => {
      if (newPassword) {
        try {
          const result = await this.authService.completeNewPasswordChallenge(newPassword);
          this.handleSuccessfulLogin(result);
        } catch (error: any) {
          console.error('Failed to set new password:', error);
          let errorMessage = 'Failed to set new password.';
          if (error.message) {
            errorMessage = error.message;
          }
          alert(errorMessage);
        }
      } else {
        // User cancelled password change
      }
    });
  }

  /**
   * Handles successful login by storing tokens and navigating
   */
  private handleSuccessfulLogin(result: any): void {
    const idToken = result.getIdToken().getJwtToken();
    const accessToken = result.getAccessToken().getJwtToken();
    const refreshToken = result.getRefreshToken().getToken();

    localStorage.setItem('id_token', idToken);
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);

    this.router.navigateByUrl(this.consumeReturnUrl());
  }

  forgotPassword() {
    this.dialog.open(PasswordResetDialogComponent, {
      width: '400px',
      data: { username: this.username }
    });
  }
}